/**
 * Labels for the claims → encounters swap.
 *
 * INS-581 swapped the underlying *data* from claims to encounters for cohort
 * (encounter) searches behind the `show-encounters-totals` flag, deliberately leaving
 * the "Claims" labels in place. These helpers are the label half of that work: when the
 * flag is on and the slot is a cohort/encounter search, the visible metric word becomes
 * "Encounters".
 *
 * The signature mirrors `claimsOrEncounters(data, isCohort, showEncountersTotals)` in
 * `src/components/Profiles/shared/widgets/utils.ts` so each call site passes the same
 * arguments it already passes for the value swap.
 */

export const CLAIMS_LABEL = 'Claims';
export const ENCOUNTERS_LABEL = 'Encounters';

/**
 * The user-facing definition of an encounter, used as tooltip copy on the prominent
 * "Encounters" labels so the volume difference is explained where it is most visible.
 */
export const ENCOUNTERS_DEFINITION =
  'Encounters are procedures and/or diagnoses for the same patient, on the same day, with the same provider. This can span across multiple claims.';

/**
 * Whether a saved volume is a cohort (encounter) search that actually carries its
 * encounters annotation. A cohort only gains its claims/patients/encounters annotations
 * once it is `READY`; other statuses (PROCESSING/ERROR/DEACTIVATED) have no encounters
 * value — and Discovery lets a non-READY cohort be selected (deactivated ones are kept
 * so they can be reactivated).
 *
 * The label must be gated on the same condition as the value/sort swap, otherwise a
 * non-READY cohort reads "Encounters" over a claims number.
 *
 * @param volume - the applied saved volume (`useSavedVolume().appliedSavedVolume`)
 *
 * @example
 * const label = claimsMetricLabel(isReadyCohortVolume(appliedSavedVolume), !!flag);
 */
export const isReadyCohortVolume = (
  volume: { volumeType?: string; status?: string } | null | undefined
): boolean => volume?.volumeType === 'cohort' && volume?.status === 'READY';

/**
 * Returns the metric label for a claims-role value.
 *
 * @param isCohort - whether the slot/column/search is a cohort (encounter) one
 * @param showEncountersTotals - the LaunchDarkly flag value
 * @returns `'Encounters'` for flagged cohort slots, otherwise `'Claims'`
 *
 * @example
 * const label = claimsMetricLabel(volumeType === 'cohort', !!showEncountersTotals);
 */
export const claimsMetricLabel = (
  isCohort: boolean,
  showEncountersTotals: boolean
): string =>
  showEncountersTotals && isCohort ? ENCOUNTERS_LABEL : CLAIMS_LABEL;

/**
 * Resolves the claims-role **value and its label together**, so the two cannot diverge.
 *
 * Prefer this over pairing `claimsOrEncounters(...)` with {@link claimsMetricLabel} at a
 * call site that has the data in hand. `claimsOrEncounters` falls back to `claims` when
 * `encounters` is absent, but a label derived only from `(isCohort, flag)` doesn't know
 * that happened — so a missing `encounters` field would render a claims number under an
 * "Encounters" label.
 *
 * Value semantics are identical to `claimsOrEncounters` (a nullish `encounters` falls
 * back to `claims`, and `0` is a real value); only the label is newly honest.
 *
 * @param data - the volume payload (`{ claims, encounters }`)
 * @param isCohort - whether the slot/column/search is a cohort (encounter) one
 * @param showEncountersTotals - the LaunchDarkly flag value
 *
 * @example
 * const { value, label } = resolveClaimsMetric(volume, !!cohortId, !!flag);
 */
export const resolveClaimsMetric = (
  data:
    | { claims?: number | string | null; encounters?: number | string | null }
    | null
    | undefined,
  isCohort: boolean,
  showEncountersTotals: boolean
): {
  value: number | string | null | undefined;
  label: string;
  usedEncounters: boolean;
} => {
  const usedEncounters =
    showEncountersTotals && isCohort && data?.encounters != null;

  return {
    value: usedEncounters ? data?.encounters : data?.claims,
    label: usedEncounters ? ENCOUNTERS_LABEL : CLAIMS_LABEL,
    usedEncounters,
  };
};

/**
 * Lowercase variant of {@link claimsMetricLabel}, for the metric word inside prose
 * (tooltips, sentences).
 *
 * @example
 * `${row.name} has ${volume} ${claimsMetricWord(isCohort, flag)} for ${searchName}`;
 */
export const claimsMetricWord = (
  isCohort: boolean,
  showEncountersTotals: boolean
): string => claimsMetricLabel(isCohort, showEncountersTotals).toLowerCase();
